import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Accordion, Alert, ContainerTab, ContainerTabs, Dialog, DialogActions, DialogContent, Loading, TextArea, Typography, } from '@guildeducationinc/recess';
import ArrowBackIcon from '@guildeducationinc/recess/icons/ArrowBackIcon';
import WarningAmberIcon from '@guildeducationinc/recess/icons/WarningAmberIcon';
import { Badge } from '@mui/material';
import { UserActivities } from '@src/components/Activity/UserActivities';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { PromptCategory } from '@src/components/Admin/AiSummary/typesAi';
import { ViewEmployeeDetailsButton } from '@src/components/Admin/EmployeeDetailsButton/EmployeeDetailsButton';
import { ViewEmployeeSnapshotButton } from '@src/components/Admin/ViewEmployeeSnapshotButton/ViewEmployeeSnapshotButton';
import { SummaryCard } from '@src/components/Employee/SummaryCard';
import { ClaimsCommentsList } from '@src/components/PaymentRequest/ClaimsCommentsList';
import { PaymentOverview } from '@src/components/PaymentRequest/PaymentOverview';
import { PaymentRequestInProcessTag } from '@src/components/PaymentRequest/PaymentRequestInProcessTag';
import { ProgramOverview } from '@src/components/PaymentRequest/ProgramOverview';
import { PaymentRequestActions } from '@src/components/PaymentRequestActions';
import { UploadDocuments } from '@src/components/UploadDocuments';
import { DirtyContext } from '@src/contexts/DirtyContextProvider';
import { ProgramContextProvider } from '@src/contexts/ProgramContextProvider';
import { APPLIED_RULES } from '@src/graphql/queries/appliedRule';
import { APPROVE_PAYMENT_REQUEST, PAYMENT_REQUEST, SET_PAYMENT_REQUEST_IN_PROCESS, UPDATE_PAYMENT_REQUEST_DOCUMENTS, UPDATE_PAYMENT_REQUEST_STATUS, } from '@src/graphql/queries/paymentRequest';
import { UPDATE_PROGRAM_SCHOOL_COUNTRY } from '@src/graphql/queries/program';
import { useAuth } from '@src/hooks/useAuth';
import { useClaimsComments } from '@src/hooks/useClaimsComments';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { useCheckPaymentRequestDuplicates } from '@src/hooks/usePaymentRequestDuplicates';
import { AcademicPolicyType, PaymentRequestStatus, RuleType, } from '@src/types';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { getFullName } from '@src/utilities/nameHelper';
import { GlobalPermission } from '@src/utilities/permissions';
import classNames from 'classnames';
import { omit, some } from 'lodash';
import { DocumentEntity, DocumentViewerTabs, getBasePath, Messages, MessageType, TrackableButton, useAiSummary, useLocale, useRules, } from 'pe-core-frontend';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AiSummary } from '../Admin/AiSummary/AiSummary';
import { PaymentRequestStatusHistory } from '../Admin/PaymentRequestStatusHistory';
import { ReadonlyRuleSnapshot } from '../Admin/PolicyBuilder/ReadonlyRuleSnapshot';
import { TuitionCalculator } from '../Admin/TuitionCalculator/TuitionCalculator';
import { PaymentRequestItems } from '../PaymentRequestItems/PaymentRequestItems';
import { PaymentRequestStatusTag } from '../PaymentRequestStatusTag';
import styles from './PaymentRequest.module.scss';
const prepareItem = (item, status) => {
    return {
        ...item,
        tempId: item.tempId || item.id?.toString() || `temp-${Date.now()}-${Math.random()}`,
        amountApproved: item.amountApproved ??
            ([PaymentRequestStatus.DOCUMENT_REVIEW, PaymentRequestStatus.ON_HOLD].includes(status)
                ? item.amountRequested
                : undefined),
    };
};
export const prepareItems = (paymentRequest) => {
    const items = paymentRequest.paymentRequestItems.map(item => prepareItem(item, paymentRequest.status));
    return { ...paymentRequest, paymentRequestItems: items };
};
export const PaymentRequest = ({ id }) => {
    const { t } = useLocale();
    const { hasPermission } = useAuth();
    const { data, error, loading } = useQuery(PAYMENT_REQUEST, {
        variables: { id },
    });
    // Fetch policy exceptions (overrides) applied to this payment request
    const { data: appliedRulesData } = useQuery(APPLIED_RULES, {
        variables: {
            spec: {
                programOrPaymentRequestId: id,
                isException: true,
                includeRuleSnapshot: true,
            },
        },
        skip: !id,
    });
    const [paymentRequest, setPaymentRequest] = useState(data?.paymentRequest);
    // Track if we've already checked for duplicates for this payment request
    const hasCheckedDuplicates = useRef(false);
    // Refs for scrolling columns to top after actions
    const leftColRef = useRef(null);
    const rightColRef = useRef(null);
    const scrollColumnsToTop = useCallback(() => {
        if (leftColRef.current) {
            leftColRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        if (rightColRef.current) {
            rightColRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);
    // Admin-facing duplicate check using the hook
    const { duplicates, checkForDuplicates } = useCheckPaymentRequestDuplicates({
        paymentRequest,
    });
    useEffect(() => {
        if (!loading && data?.paymentRequest) {
            setPaymentRequest(prepareItems(data.paymentRequest));
        }
    }, [data, loading]);
    // Reset duplicate check flag when payment request ID changes
    useEffect(() => {
        hasCheckedDuplicates.current = false;
    }, [paymentRequest?.id]);
    // Check for duplicates when payment request is first loaded
    // Only run once per payment request after items have been prepared with tempIds
    useEffect(() => {
        if (paymentRequest &&
            paymentRequest.paymentRequestItems.length > 0 &&
            paymentRequest.id &&
            // Ensure items have tempIds (meaning prepareItems has run)
            paymentRequest.paymentRequestItems.every(item => item.tempId) &&
            // Only check once per payment request
            !hasCheckedDuplicates.current) {
            checkForDuplicates();
            hasCheckedDuplicates.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentRequest?.id, paymentRequest?.paymentRequestItems]); // Depend on items so it runs after prepareItems
    const [approvePaymentRequest] = useMutation(APPROVE_PAYMENT_REQUEST);
    const [updateStatus] = useMutation(UPDATE_PAYMENT_REQUEST_STATUS);
    const [updateDocuments] = useMutation(UPDATE_PAYMENT_REQUEST_DOCUMENTS);
    const [updateProgramSchoolCountry] = useMutation(UPDATE_PROGRAM_SCHOOL_COUNTRY);
    const [isSaving, setIsSaving] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isRetranslating, setIsRetranslating] = useState(false);
    const navigate = useNavigate();
    const { setIsDirty, setUseMui } = useContext(DirtyContext);
    const { items: rules } = useRules({
        clientId: paymentRequest?.clientId,
        ruleType: RuleType.POINT_IN_TIME_ELIGIBILITY,
    });
    const { comments, loading: loadingComments, error: commentsError, refetch: refetchComments, } = useClaimsComments({ paymentRequestId: id });
    const { getPolicy } = useMyBenefits({
        program: paymentRequest?.program,
        paymentRequest: paymentRequest ?? undefined,
        skip: !paymentRequest?.id,
    });
    const snapshotsEnabled = useMemo(() => some(rules, (r) => r.isActive), [rules]);
    const hasExceptionRules = useMemo(() => appliedRulesData?.appliedRules && appliedRulesData.appliedRules.length > 0, [appliedRulesData?.appliedRules]);
    const { currentUser } = useAuth();
    const inProcessByCurrentUser = paymentRequest?.inProcess && paymentRequest?.processedById === currentUser?.id;
    const [accordionResetKey, setAccordionResetKey] = useState(0);
    const [accordionExpandAll, setAccordionExpandAll] = useState(false);
    const [showReopenDialog, setShowReopenDialog] = useState(false);
    const [reopenComment, setReopenComment] = useState('');
    const handleCollapseAll = () => {
        setAccordionExpandAll(false);
        setAccordionResetKey(prev => prev + 1);
    };
    const handleExpandAll = () => {
        setAccordionExpandAll(true);
        setAccordionResetKey(prev => prev + 1);
    };
    const { aiSummaryResponse: aiClassificationData, triggerAiProcessor } = useAiSummary({
        clientId: paymentRequest?.clientId,
        docs: paymentRequest?.documents.docs?.map(doc => doc.s3Key),
        documentEntity: DocumentEntity.PAYMENT_REQUESTS,
        documentEntityId: paymentRequest?.id.toString(),
        promptCategory: PromptCategory.TRANSLATION,
        returnMostRecent: true,
        pagination: {
            page: 1,
            pageSize: 10,
        },
    });
    const translationsByS3Key = useMemo(() => {
        const translationsByS3Key = {};
        for (const item of aiClassificationData?.getAiSummary?.items ?? []) {
            if (item.s3Key && item.llmResponse.detected_language && item.llmResponse.extracted_text) {
                translationsByS3Key[item.s3Key] = {
                    language: item.llmResponse.detected_language,
                    translation: item.llmResponse.extracted_text,
                };
            }
        }
        return translationsByS3Key;
    }, [aiClassificationData]);
    const handleRetranslate = async (s3Key) => {
        if (!paymentRequest)
            return;
        setIsRetranslating(true);
        setMessages([]);
        try {
            const success = await triggerAiProcessor({
                clientId: paymentRequest.clientId,
                docs: [s3Key],
                documentEntity: DocumentEntity.PAYMENT_REQUESTS,
                documentEntityId: parseInt(paymentRequest.id.toString()),
                promptCategory: PromptCategory.TRANSLATION,
                processDocumentsTogether: false,
            });
            setIsRetranslating(false);
            if (success) {
                setMessages([
                    {
                        value: t('Document translation has been submitted. Please refresh the page or check back in approximately 5 minutes to see the updated translation.'),
                        type: MessageType.SUCCESS,
                        params: {},
                    },
                ]);
            }
            else {
                setMessages([
                    {
                        value: t('Error retranslating document. Please try again.'),
                        type: MessageType.ERROR,
                        params: {},
                    },
                ]);
            }
        }
        catch (err) {
            setIsRetranslating(false);
            setMessages([
                {
                    value: t('Error retranslating document. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
    };
    const [setInProcess] = useMutation(SET_PAYMENT_REQUEST_IN_PROCESS);
    const handleRelease = async () => {
        setIsSaving(true);
        try {
            const status = paymentRequest?.status;
            const { data } = await setInProcess({ variables: { id, inProcess: false } });
            if (data?.setPaymentRequestInProcess?.messages) {
                setMessages(data.setPaymentRequestInProcess.messages);
            }
            else {
                navigate(`/admin/claims/${status === PaymentRequestStatus.ON_HOLD ? 'hold' : 'pending'}`);
            }
        }
        catch (e) {
            setMessages([
                {
                    value: t('Error releasing payment request. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
        finally {
            setIsSaving(false);
        }
    };
    useEffect(() => {
        setUseMui(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const canApproveClaim = hasPermission(GlobalPermission.PROCESS_CLAIMS) &&
        paymentRequest &&
        [PaymentRequestStatus.DOCUMENT_REVIEW, PaymentRequestStatus.ON_HOLD].includes(paymentRequest.status) &&
        inProcessByCurrentUser;
    const canReopenClaim = hasPermission(GlobalPermission.PROCESS_CLAIMS) &&
        paymentRequest &&
        [
            PaymentRequestStatus.REJECTED,
            PaymentRequestStatus.CLOSED,
            PaymentRequestStatus.EXPIRED,
        ].includes(paymentRequest.status);
    const handleApproveClick = async (calculateOnly, commentContent) => {
        setIsSaving(true);
        setMessages([]);
        // Validate CGPA requirements before approval
        if (!calculateOnly && paymentRequest?.program) {
            const academicPolicy = getPolicy(RuleType.ACADEMIC, paymentRequest.program);
            if (academicPolicy?.policyType === AcademicPolicyType.CGPA) {
                const errors = [];
                if (!paymentRequest.cgpa ||
                    (typeof paymentRequest.cgpa === 'string' && paymentRequest.cgpa.trim() === '')) {
                    errors.push('CGPA is required');
                }
                else if (isNaN(Number(paymentRequest.cgpa))) {
                    errors.push('must be a decimal number');
                }
                if (paymentRequest.creditsEarnedSoFar === null ||
                    paymentRequest.creditsEarnedSoFar === undefined) {
                    errors.push('Credits earned so far is required');
                }
                if (errors.length > 0) {
                    setMessages(errors.map(error => ({ value: t(error), type: MessageType.ERROR, params: {} })));
                    setIsSaving(false);
                    return;
                }
            }
        }
        // Validate approved amounts before sending to the GraphQL Decimal field.
        // Comma is a valid decimal separator in some locales, so we reject rather
        // than guess at stripping it (see MX-3918).
        const hasInvalidAmountApproved = some(paymentRequest?.paymentRequestItems ?? [], (item) => {
            const raw = item.amountApproved;
            if (raw === undefined || raw === null)
                return false;
            return isNaN(Number(raw));
        });
        if (hasInvalidAmountApproved) {
            setMessages([{ value: t('must be a decimal number'), type: MessageType.ERROR, params: {} }]);
            setIsSaving(false);
            return;
        }
        try {
            const input = prepareForSave(paymentRequest);
            const result = await approvePaymentRequest({
                variables: {
                    input: {
                        ...omit(input, 'program', 'client', 'processedBy', 'processedById', 'submittedAt', 'capYear', 'capSegment', 'amountToPay', 'workflowRunId'),
                        paymentRequestItems: input.paymentRequestItems.map((item) => omit(item, 'feeType', 'capYear', 'capSegment', 'amountToPay', 'status', 'calculations')),
                        cgpa: input.cgpa ? input.cgpa : undefined,
                        commentContent,
                    },
                    calculateOnly,
                },
            });
            if (result.data?.approvePaymentRequest?.messages) {
                setMessages(result.data.approvePaymentRequest.messages);
            }
            if (result.data?.approvePaymentRequest.paymentRequest && calculateOnly) {
                // Merge calculated fields from server with existing local state
                // to preserve user selections (school country, grading scales, etc.)
                const serverResponse = result.data.approvePaymentRequest.paymentRequest;
                setPaymentRequest(prev => {
                    if (!prev)
                        return prepareItems(serverResponse);
                    // Update payment request items with calculated fields while preserving user edits
                    const mergedItems = prev.paymentRequestItems.map(localItem => {
                        const serverItem = serverResponse.paymentRequestItems.find((si) => si.id === localItem.id || si.tempId === localItem.tempId);
                        if (serverItem) {
                            // Merge: keep local edits but update calculated fields from server
                            return {
                                ...localItem,
                                // Update calculated fields from server
                                amountToPay: serverItem.amountToPay,
                                status: serverItem.status,
                                calculations: serverItem.calculations,
                            };
                        }
                        return localItem;
                    });
                    return {
                        ...prev,
                        // Update calculated fields from server
                        amountApproved: serverResponse.amountApproved,
                        amountToPay: serverResponse.amountToPay,
                        // Preserve user edits to program (including school country) and items
                        paymentRequestItems: mergedItems,
                    };
                });
            }
            else {
                setIsDirty(false);
                await refetchComments();
                scrollColumnsToTop();
            }
        }
        catch (e) {
            setMessages([
                {
                    value: t('Error processing payment request. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleReopenClick = () => {
        setShowReopenDialog(true);
        setReopenComment('');
        setMessages([]);
    };
    const handleReopenSubmit = async () => {
        setMessages([]);
        if (!reopenComment.trim()) {
            setMessages([
                {
                    value: t('A comment is required when reopening a claim'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
            return;
        }
        if (paymentRequest) {
            setIsSaving(true);
            try {
                const result = await updateStatus({
                    variables: {
                        id: paymentRequest.id,
                        status: PaymentRequestStatus.DOCUMENT_REVIEW,
                        commentContent: reopenComment,
                    },
                });
                if (result.data?.updatePaymentRequestStatus?.messages) {
                    setMessages(result.data.updatePaymentRequestStatus.messages);
                }
                else {
                    setShowReopenDialog(false);
                    setReopenComment('');
                    // Refetch comments to show the new comment
                    await refetchComments();
                }
            }
            catch (e) {
                setMessages([
                    {
                        value: t('Error reopening payment request. Please try again.'),
                        type: MessageType.ERROR,
                        params: {},
                    },
                ]);
            }
            finally {
                setIsSaving(false);
            }
        }
    };
    const handleUploadedDocument = async (documents) => {
        setMessages([]);
        setPaymentRequest({
            ...prepareItems(paymentRequest),
            documents: { docs: documents },
        });
        try {
            const result = await updateDocuments({
                variables: {
                    id: paymentRequest.id,
                    input: { docs: documents.map(x => omit(x, ['__typename'])) },
                },
            });
            if (result.data?.updatePaymentRequestDocuments?.messages) {
                setMessages(result.data.updatePaymentRequestDocuments.messages);
            }
        }
        catch (e) {
            setMessages([
                {
                    value: t('Error uploading document for payment request. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Claims'), subTitle: paymentRequest?.program && (_jsxs("div", { className: 'flex gap-4 items-center', children: [_jsx(Typography, { variant: 'title3', children: getFullName(paymentRequest.program.employee) }), _jsx(PaymentRequestStatusTag, { paymentRequest: paymentRequest, size: 'large' }), !inProcessByCurrentUser &&
                            paymentRequest.inProcess &&
                            paymentRequest.processedBy && (_jsx(PaymentRequestInProcessTag, { paymentRequest: paymentRequest, size: 'large' }))] })), actions: _jsxs("div", { className: styles.adminNavActions, children: [canReopenClaim && (_jsx(TrackableButton, { enText: 'Reopen', tag: 'process_claim', buttonSize: 'small', onClick: handleReopenClick, children: t('Reopen') })), canApproveClaim && (_jsx("div", { className: 'flex gap-4', children: _jsx(TrackableButton, { enText: 'Release', tag: 'process_claim', buttonSize: 'small', appearance: 'outline', disabled: isSaving, onClick: handleRelease, children: t('Release') }) })), _jsxs(Link, { to: '#', className: 'flex ml-8', onClick: () => navigate(-1), children: [_jsx(ArrowBackIcon, { size: 20 }), " ", t('Back')] })] }) }), _jsx("div", { className: styles.container, children: _jsxs(Loading, { label: t('Payment Request'), loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: [_jsx("div", { children: error && (_jsx(Alert, { hideClose: true, display: true, type: 'danger', children: t('Failed to load payment request') })) }), paymentRequest && (_jsxs("div", { className: styles.layoutContainer, children: [_jsxs("div", { ref: leftColRef, className: classNames(styles.leftCol, 'bg-white flex flex-col gap-4 border-r border-gray-300'), children: [_jsx("div", { className: 'mt-2 ml-2', children: _jsx(TrackableButton, { btnType: 'link', enText: 'View details', tag: 'process_claim', href: `${getBasePath()}/policy?clientId=${paymentRequest.clientId}`, target: '_blank', className: 'flex items-center gap-1', children: _jsx(Typography, { variant: 'title3', children: paymentRequest.client.name }) }) }), paymentRequest.program && (_jsxs(ProgramContextProvider, { program: paymentRequest.program, paymentRequest: paymentRequest, children: [_jsx(ProgramOverview, { program: paymentRequest.program, refreshToken: isSaving, editable: !!canApproveClaim, onProgramChange: async (updater) => {
                                                        const updatedProgram = updater(paymentRequest.program);
                                                        setPaymentRequest(prev => ({
                                                            ...prev,
                                                            program: updatedProgram,
                                                        }));
                                                        setIsDirty(true);
                                                        // Save program school country to database
                                                        try {
                                                            // Determine if this is an "other" school by checking which fields were updated
                                                            const isOtherSchool = !!updatedProgram.otherSchoolCountryCode;
                                                            const countryCode = isOtherSchool
                                                                ? updatedProgram.otherSchoolCountryCode
                                                                : updatedProgram.schoolCountryCode;
                                                            const countryId = isOtherSchool
                                                                ? updatedProgram.otherSchoolCountryId
                                                                : updatedProgram.schoolCountryId;
                                                            await updateProgramSchoolCountry({
                                                                variables: {
                                                                    input: {
                                                                        id: updatedProgram.id,
                                                                        countryCode,
                                                                        countryId,
                                                                        isOtherSchool,
                                                                    },
                                                                },
                                                            });
                                                        }
                                                        catch (e) {
                                                            setMessages([
                                                                {
                                                                    value: t('Error saving program changes. Please try again.'),
                                                                    type: MessageType.ERROR,
                                                                    params: {},
                                                                },
                                                            ]);
                                                        }
                                                    } }), _jsx(PaymentOverview, { paymentRequest: paymentRequest, onCalculate: () => handleApproveClick(true), disabled: isSaving, onChange: value => {
                                                        setPaymentRequest(value);
                                                        setIsDirty(true);
                                                    } }), _jsx(PaymentRequestItems, { paymentRequest: paymentRequest, editable: (canApproveClaim &&
                                                        [
                                                            PaymentRequestStatus.DOCUMENT_REVIEW,
                                                            PaymentRequestStatus.ON_HOLD,
                                                        ].includes(paymentRequest.status)) ||
                                                        false, onChange: value => {
                                                        setPaymentRequest(value);
                                                        setIsDirty(true);
                                                    }, duplicates: duplicates })] }))] }), _jsxs("div", { ref: rightColRef, className: styles.rightCol, children: [_jsx("div", { children: _jsx(Messages, { className: 'px-5', messages: messages }) }), _jsxs(ContainerTabs, { onActive: () => setMessages([]), children: [_jsxs(ContainerTab, { title: t('Documents'), children: [canApproveClaim && (_jsx("div", { className: styles.uploadContainer, children: _jsx(UploadDocuments, { documents: paymentRequest.documents.docs, btnText: '', title: ' ', infoText: '', explanation: '', onChange: documents => handleUploadedDocument(documents), uploadOnly: true, useDragAndDrop: false }) })), _jsx("div", { className: styles.documents, children: paymentRequest.documents.docs.length > 0 && (_jsx(DocumentViewerTabs, { docs: paymentRequest.documents.docs, className: styles.documentViewer, translationsByS3Key: translationsByS3Key, onRetranslate: handleRetranslate, isRetranslating: isRetranslating })) })] }), _jsx(ContainerTab, { title: _jsx("div", { children: _jsx(Badge, { badgeContent: comments.length, color: 'default', sx: {
                                                                '.MuiBadge-badge': {
                                                                    background: 'white',
                                                                    right: comments.length > 99 ? -16 : -12,
                                                                },
                                                            }, children: t('Communications') }) }), children: _jsx(ClaimsCommentsList, { comments: comments, loading: loadingComments, error: commentsError, onTranslate: refetchComments }) }), _jsx(ContainerTab, { title: t('Employee'), children: paymentRequest && (_jsxs("div", { className: 'flex flex-col gap-3', children: [_jsx(SummaryCard, { headingClass: 'border-b border-gray-900 pb-2 mb-2 flex justify-between items-center', itemsClass: '!flex-row justify-between', details: [
                                                                    {
                                                                        label: t('Employee ID'),
                                                                        value: paymentRequest.program.employee.uniqueId,
                                                                    },
                                                                    {
                                                                        label: t('Name'),
                                                                        value: getFullName(paymentRequest.program.employee),
                                                                    },
                                                                    {
                                                                        label: t('Email'),
                                                                        value: paymentRequest.program.employee.email,
                                                                    },
                                                                ], actions: _jsxs("div", { children: [_jsx(ViewEmployeeDetailsButton, { employeeId: paymentRequest.program.employeeId, clientId: paymentRequest.clientId }), snapshotsEnabled && paymentRequest.program.employee && (_jsx(ViewEmployeeSnapshotButton, { employee: paymentRequest.program.employee }))] }) }), _jsx(UserActivities, { clientId: paymentRequest.clientId, hideAdminNav: true, employeeId: paymentRequest.program?.employeeId?.toString() })] })) }), _jsx(ContainerTab, { title: t('Tuition Calculator'), children: paymentRequest && (_jsx("div", { className: 'flex flex-col gap-3', children: _jsx(TuitionCalculator, { paymentRequest: paymentRequest }) })) }), _jsx(ContainerTab, { title: t('AI Summary'), children: paymentRequest && (_jsx("div", { className: 'flex flex-col gap-3', children: _jsx(AiSummary, { paymentRequest: paymentRequest }) })) }), _jsxs(ContainerTab, { title: _jsxs("span", { className: 'flex gap-1 items-center', children: [t('Exception Rules'), hasExceptionRules && (_jsx("span", { children: _jsx(WarningAmberIcon, { size: 16, color: 'red' }) }))] }), children: [!hasExceptionRules && (_jsx("div", { className: 'text-xl p-4', children: t('No exception rule applied') })), hasExceptionRules && (_jsxs("div", { className: 'flex flex-col gap-3 p-4', children: [_jsxs("div", { className: 'flex justify-between items-center mb-2', children: [_jsx("div", { className: 'text-xl font-semibold', children: t('Exception rule(s) applied') }), _jsxs("div", { className: 'flex gap-2', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Expand All', tag: 'exception_rules', variant: 'outlined', size: 'small', onClick: handleExpandAll, children: t('Expand All') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Collapse All', tag: 'exception_rules', variant: 'outlined', size: 'small', onClick: handleCollapseAll, children: t('Collapse All') })] })] }), _jsx("div", { className: 'flex flex-col gap-2', children: appliedRulesData.appliedRules.map((rule) => (_jsx(Accordion, { title: `${rule.paymentRequestId ? t('Payment Request') : t('Program')} - ${t(`ruleType.${rule.ruleType}`)} - ${rule.createdAt ? new Date(rule.createdAt).toLocaleString() : '-'}`, defaultOpen: accordionExpandAll, children: rule.ruleSnapshot && (_jsx(ReadonlyRuleSnapshot, { ruleSnapshot: rule.ruleSnapshot, clientId: paymentRequest.clientId })) }, `${rule.id}-${accordionResetKey}`))) })] }))] }), _jsx(ContainerTab, { title: t('Status History'), children: paymentRequest && (_jsx("div", { className: 'flex flex-col gap-3', children: _jsx(PaymentRequestStatusHistory, { paymentRequest: paymentRequest }) })) })] }), _jsx("div", { className: 'p-4', children: _jsx(PaymentRequestActions, { paymentRequest: paymentRequest, onSubmit: () => {
                                                    setMessages([]);
                                                    refetchComments();
                                                    scrollColumnsToTop();
                                                }, onApprove: commentContent => handleApproveClick(false, commentContent) }) })] })] }))] }) }), _jsxs(Dialog, { isOpen: showReopenDialog, title: t('Reopen Claim'), onClose: () => {
                    setShowReopenDialog(false);
                    setMessages([]);
                }, fullScreen: true, children: [_jsxs(DialogContent, { className: 'flex flex-col gap-2', children: [_jsx(Typography, { variant: 'body2', children: t('Please provide a comment explaining why this claim is being reopened. This comment will be visible to the employee.') }), _jsx(Messages, { className: 'px-0', messages: messages }), _jsx(TextArea, { fullWidth: true, required: true, label: t('Comment'), value: reopenComment, onChange: e => setReopenComment(e.currentTarget.value), placeholder: t('Enter reason for reopening...') })] }), _jsxs(DialogActions, { fullScreen: true, children: [_jsx(TrackableButton, { enText: 'Cancel', tag: 'reopen_claim', appearance: 'outline', onClick: () => setShowReopenDialog(false), disabled: isSaving, children: t('Cancel') }), _jsx(TrackableButton, { enText: 'Reopen Claim', tag: 'reopen_claim', appearance: 'primary', onClick: handleReopenSubmit, disabled: isSaving, children: t('Reopen Claim') })] })] })] }));
};
